// Reemplazo de packages/hce-react/src/LinkTabs/LinkTabs.tsx (Mantine Tabs)
// con Radix Tabs (orientation="vertical") + <Link> real de React Router en
// cada trigger (vía asChild) — así cada pestaña sigue siendo un link de
// verdad (clic derecho → abrir en pestaña nueva funciona), y el estado
// "activo" se deriva del último segmento de la URL actual, igual que el
// original.
import type { JSX } from 'react';
import { Link, useLocation } from 'react-router';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';

export interface TabDefinition {
  readonly label: string;
  readonly value: string;
}

export interface LinkTabsProps {
  readonly baseUrl: string;
  readonly tabs: TabDefinition[];
  readonly orientation?: 'horizontal' | 'vertical';
  /**
   * Query string (con `?`) que se le cuelga a TODAS las pestañas — hoy solo
   * lo usa Enfermería, para propagar el `?encounter=` de la consulta/estancia
   * abierta a los formularios que viven directamente en una pestaña (Kardex
   * 022, Intervenciones 120, Nota de turno). Ver lib/enlaceEncounter.ts.
   * Sin esto, `to` queda como antes (`{baseUrl}/{value}`, sin query).
   */
  readonly search?: string;
  /**
   * Bloqueo de salida sin firmar/descartar (pedido explícito de Sue,
   * 2026-09-27, ver PatientChartOutletContext.reportarPendienteFirma) —
   * llamado antes de dejar seguir el click de cada pestaña; si devuelve
   * `false` (el practicante eligió "quedarse" en el diálogo de
   * confirmación), se cancela la navegación. Sin esto (uso normal fuera de
   * la ficha de paciente), el click navega siempre.
   */
  readonly confirmarSalida?: () => boolean;
}

export function LinkTabs({ baseUrl, tabs, orientation = 'horizontal', search, confirmarSalida }: LinkTabsProps): JSX.Element {
  const location = useLocation();
  // Primer segmento relativo a `baseUrl` (no el último de toda la URL —
  // bug real en vivo 2026-09-02: con eso, rutas anidadas de más de un nivel
  // bajo `baseUrl` que no son pestañas del sidebar, como ".../consulta" o
  // ".../formulario/001", tomaban su ÚLTIMO segmento ("consulta"/"001"), no
  // matcheaban ninguna pestaña, y caían al `?? tabs[0]` de abajo — el
  // sidebar marcaba "Resumen" como activo aunque la página mostrada fuera
  // otra por completo). Segmento vacío (ruta índice, exactamente
  // `baseUrl`) es el único caso que debe caer en la primera pestaña —
  // cualquier OTRO segmento sin match no marca ninguna pestaña.
  const relativo = location.pathname.startsWith(baseUrl) ? location.pathname.slice(baseUrl.length) : location.pathname;
  const segment = relativo.replace(/^\/+/, '').split('/')[0]?.toLowerCase();
  const current = !segment ? tabs[0]?.value : tabs.find((t) => t.value.toLowerCase() === segment)?.value;

  return (
    <Tabs value={current} orientation={orientation}>
      <TabsList className={orientation === 'vertical' ? 'w-full' : undefined}>
        {tabs.map((t) => (
          <TabsTrigger key={t.value} value={t.value} asChild>
            <Link to={`${baseUrl}/${t.value}${search ?? ''}`} onClick={(e) => { if (confirmarSalida && !confirmarSalida()) e.preventDefault(); }}>
              {t.label}
            </Link>
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}
